import { Link, useParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Seo, faqJsonLd, breadcrumbJsonLd } from "../../components/Seo";
import { ServiceIcon } from "../../components/ServiceIcon";
import { Faq } from "../../components/Faq";
import { CallCta } from "../../components/CallCta";
import { Testimonials } from "../../components/Testimonials";
import { getServiceFr, QC_CITIES, BUSINESS_FR, SERVICES_FR } from "../../data/site-fr";
import NotFound from "../NotFound";

const SERVICE_IMGS = [
  "https://images.pexels.com/photos/8285775/pexels-photo-8285775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/12492185/pexels-photo-12492185.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwc3RhbmRpbmclMjBwcm9mZXNzaW9uYWwlMjB1bmlmb3JtfGVufDB8fHx8MTc5MjM3NDI3Nnww&ixlib=rb-4.1.0&q=85",
];

export default function ServicePageFr() {
  const { slug } = useParams();
  const service = getServiceFr(slug);
  if (!service) return <NotFound />;
  const img = SERVICE_IMGS[SERVICES_FR.indexOf(service) % SERVICE_IMGS.length];

  return (
    <div data-testid={`service-fr-page-${service.slug}`}>
      <Seo
        lang="fr-CA"
        title={`${service.title} | 1st Nations Security`}
        description={`${service.shortDesc} Répartition 24/7 au Québec et au Manitoba. Appelez le ${BUSINESS_FR.phone}.`}
        path={`/francais/services-de-securite/${service.slug}/`}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "Service", serviceType: service.title, provider: { "@type": "LocalBusiness", name: BUSINESS_FR.name, telephone: BUSINESS_FR.phone }, areaServed: [{ "@type": "AdministrativeArea", name: "Québec, Canada" }, { "@type": "AdministrativeArea", name: "Manitoba, Canada" }] },
          faqJsonLd(service.faqs),
          breadcrumbJsonLd([{ name: "Accueil", path: "/francais" }, { name: "Services de sécurité", path: "/francais/services-de-securite" }, { name: service.title, path: `/francais/services-de-securite/${service.slug}` }]),
        ]}
      />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d'Ariane">
            <Link to="/francais" className="hover:text-[#FF9100]" data-testid="breadcrumb-fr-accueil">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/francais/services-de-securite" className="hover:text-[#FF9100]" data-testid="breadcrumb-fr-services">Services de sécurité</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">{service.title}</span>
          </nav>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5 mb-5">
                <ServiceIcon name={service.icon} className="w-4 h-4 text-[#FF9100]" />
                <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100]">{service.category}</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">
                {service.title}
              </h1>
              {service.intro.map((p, i) => (
                <p key={i} className="text-gray-400 leading-relaxed mb-4">{p}</p>
              ))}
            </div>
            <div>
              <img src={img} alt={`${service.title} par 1st Nations Security`} className="rounded-2xl border border-white/10 w-full object-cover aspect-[4/3]" />
              <div className="mt-6 bg-[#14161C] border border-white/10 rounded-2xl p-6">
                <h2 className="font-heading text-lg font-semibold text-white mb-4">Ce qui est inclus</h2>
                <ul className="space-y-3">
                  {service.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-300" data-testid={`service-fr-feature-${i}`}>
                      <Check className="w-4 h-4 text-[#FF9100] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0A0B0E]" data-testid="service-fr-coverage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
            {service.title} — Villes Desservies
          </h2>
          <p className="text-gray-400 max-w-3xl mb-8 leading-relaxed">
            Nous offrons ce service dans les grandes villes du Québec et du Manitoba. Sélectionnez votre ville pour voir les détails locaux, ou appelez le {BUSINESS_FR.phone} pour un service partout en province.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {QC_CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/francais/villes/${c.slug}`}
                data-testid={`service-fr-ville-link-${c.slug}`}
                className="group bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-lg px-3 py-3 transition-colors duration-200"
              >
                <p className="text-sm text-gray-300 group-hover:text-[#FF9100] transition-colors">{service.title} {c.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="service-fr-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-8">
            {service.title} — FAQ
          </h2>
          <Faq items={service.faqs} testidPrefix={`${service.slug}-faq`} />
          <div className="mt-8">
            <Link to="/francais/services-de-securite" data-testid="service-fr-back-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">
              <ArrowRight className="w-4 h-4 rotate-180" /> Tous les services de sécurité
            </Link>
          </div>
        </div>
      </section>

      <Testimonials fr />
      <CallCta heading={`Besoin de : ${service.title}?`} sub={`Appelez le ${BUSINESS_FR.phone} ou écrivez à ${BUSINESS_FR.email} — répartition 24/7.`} testid="service-fr-cta" />
    </div>
  );
}
